import React, { FC, useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import styles from './Cart.module.scss';
import { ReactComponent as CartIcon } from '../../../assets/icons/cart-icon.svg';

interface IProps {
    count: number;
    /** An order is still running even though the basket is empty. Without
     *  this the button would hide itself and the guest would have no way
     *  back to the order they just placed. */
    hasActiveOrder: boolean;
    onOpen: () => void;
}

function prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const FloatingCartButton: FC<IProps> = ({ count, hasActiveOrder, onOpen }) => {
    const [reduceMotion, setReduceMotion] = useState(prefersReducedMotion);
    const prevCount = useRef(count);
    const [badge, badgeApi] = useSpring(() => ({ scale: 1 }));

    const appear = useSpring({
        from: { scale: 0.7, opacity: 0 },
        to: { scale: 1, opacity: 1 },
        immediate: reduceMotion,
        config: { tension: 380, friction: 22 },
    });

    useEffect(() => {
        const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = (event: MediaQueryListEvent) => setReduceMotion(event.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    useEffect(() => {
        if (prevCount.current === count || reduceMotion) {
            prevCount.current = count;
            return;
        }
        prevCount.current = count;
        void badgeApi.start({
            from: { scale: 1.2 },
            to: { scale: 1 },
            config: { tension: 420, friction: 16 },
        });
    }, [count, badgeApi, reduceMotion]);

    if (count === 0 && !hasActiveOrder) return null;

    return (
        <animated.button
            type="button"
            className={styles.floatingButton}
            onClick={onOpen}
            style={{
                opacity: appear.opacity,
                transform: appear.scale.to((value) => `scale(${value})`),
            }}
        >
            <span className={styles.iconWrap}>
                <CartIcon className={styles.cartIcon} />
                {count > 0 ? (
                    <animated.span
                        className={styles.badge}
                        style={{ transform: badge.scale.to((value) => `scale(${value})`) }}
                    >
                        {count}
                    </animated.span>
                ) : (
                    <span className={styles.statusDot} />
                )}
            </span>
        </animated.button>
    );
};
